import { useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

// Resize a data URL image to fit within maxSide (longest edge), preserving aspect ratio.
export async function resizeDataUrl(dataUrl, maxSide, mime) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const outMime = mime === "image/png" ? "image/png" : "image/jpeg";
      resolve(c.toDataURL(outMime, 0.9));
    };
    img.onerror = () => reject(new Error("image load failed"));
    img.src = dataUrl;
  });
}

/**
 * Shared attach-image control used by Refinery / Coach / AdLab.
 * Props:
 *  - image: { base64, mime, dataUrl, name } | null
 *  - onChange: (imageObj|null) => void
 *  - label: string shown above the drop zone
 *  - testIdPrefix: string, e.g. "coach"
 */
export const ImageAttach = ({ image, onChange, label = "reference image (optional)", testIdPrefix = "attach" }) => {
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) {
      toast.error("only png, jpg or webp allowed");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("image too large (15MB max)");
      return;
    }
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error("read failed"));
        r.readAsDataURL(f);
      });
      const resized = await resizeDataUrl(dataUrl, 1600, f.type);
      const [meta, b64] = resized.split(",");
      const mime = meta.match(/data:([^;]+)/)[1];
      onChange({ base64: b64, mime, dataUrl: resized, name: f.name });
    } catch (err) {
      toast.error("couldn't read image: " + err.message);
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-image-block`}>
      <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</label>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-image-input`}
      />
      {!image ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testIdPrefix}-image-upload-btn`}
          className="mt-2 w-full flex items-center justify-center gap-2 border border-dashed border-white/15 hover:border-[color:var(--accent-matrix)] text-neutral-400 hover:text-[color:var(--accent-matrix)] py-3 text-xs font-mono duration-150"
          style={{ transitionProperty: "border-color, color" }}
        >
          <ImagePlus className="w-4 h-4" />
          attach a screenshot, mockup, or reference photo
        </button>
      ) : (
        <div className="mt-2 flex items-start gap-3 border border-white/10 p-3 bg-black" data-testid={`${testIdPrefix}-image-preview`}>
          <img src={image.dataUrl} alt="preview" className="w-16 h-16 object-cover border border-white/10" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono text-neutral-300 truncate">{image.name}</div>
            <div className="text-[10px] text-neutral-500 mt-1">
              {image.mime} · {(image.base64.length * 3 / 4 / 1024).toFixed(0)} KB
            </div>
            <button
              onClick={() => onChange(null)}
              data-testid={`${testIdPrefix}-image-remove-btn`}
              className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[color:var(--accent-amber)]"
            >
              <X className="w-3 h-3" /> remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
